import { useCallback, useEffect, useRef, useState } from "react"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import { useAppSession } from "~/ui-rtk/pages/Auth/useCubeApiToken"
import { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import { SerializableColumnConfig } from "~/ui-rtk/utils/csv-export"

export interface ExportProgress {
  progress: number
  message?: string
}

export interface ExportResult {
  downloadUrl: string
  filename: string
  recordCount: number
}

export interface ExportOptions {
  filename?: string
  delimiter?: string
  includeHeaders?: boolean
  timeoutSeconds?: number
  compareQuery?: TCubeQuery
  /** Run the query exactly as given, page limit and offset included. */
  keepPagination?: boolean
}

export function useCSVExportWorker() {
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState<ExportProgress>({ progress: 0 })
  const [error, setError] = useState<string | null>(null)
  const workerRef = useRef<Worker | null>(null)
  const { session } = useAppSession()

  const handleExportComplete = useCallback((result: ExportResult) => {
    // Create a temporary link to download the file
    const link = document.createElement("a")
    link.href = result.downloadUrl
    link.download = result.filename
    link.style.display = "none"

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the blob URL
    setTimeout(() => {
      URL.revokeObjectURL(result.downloadUrl)
    }, 100)
  }, [])

  // Initialize worker
  useEffect(() => {
    if (!workerRef.current) {
      workerRef.current = new Worker("/workers/csv-export-worker.js")

      workerRef.current.onmessage = event => {
        const { type, payload } = event.data

        switch (type) {
          case "CUBE_API_INITIALIZED":
            // Cube API initialized in worker
            break

          case "EXPORT_PROGRESS":
            setProgress(payload)
            break

          case "EXPORT_COMPLETE":
            setIsExporting(false)
            setProgress({ progress: 100, message: "Export complete" })
            handleExportComplete(payload)
            break

          case "ERROR":
            setIsExporting(false)
            setError(payload.message || "Export failed")
            break
        }
      }

      workerRef.current.onerror = () => {
        setIsExporting(false)
        setError("Worker error occurred")
      }
    }

    // Initialize cube API in worker when session is available
    if (session?.cube && workerRef.current) {
      workerRef.current.postMessage({
        type: "INIT_CUBE_API",
        payload: {
          apiUrl: `${import.meta.env.VITE_CUBE_API_URL}/cubejs-api/v1`,
          token: session.cube.token,
        },
      })
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate()
        workerRef.current = null
      }
    }
  }, [session])

  const exportToCSV = useCallback(
    (
      query: TCubeQuery,
      columnConfig: TableVisualizationColumn[] | SerializableColumnConfig[],
      options: ExportOptions = {},
    ) => {
      if (!workerRef.current) {
        throw new Error("Worker not initialized")
      }

      if (!session?.cube?.token) {
        throw new Error("No cube session available")
      }

      setIsExporting(true)
      setError(null)
      setProgress({ progress: 0, message: "Initializing export..." })

      try {
        workerRef.current.postMessage({
          type: "EXPORT_CSV",
          payload: {
            query,
            compareQuery: options.compareQuery,
            columnConfig,
            filename: options.filename,
            timeoutSeconds: options.timeoutSeconds,
            keepPagination: options.keepPagination === true,
            csvOptions: {
              delimiter: options.delimiter || ",",
              includeHeaders: options.includeHeaders !== false,
            },
          },
        })
      } catch (error) {
        setIsExporting(false)
        setError(error instanceof Error ? error.message : "Export failed")
        throw error
      }
    },
    [session],
  )

  const resetExport = useCallback(() => {
    setIsExporting(false)
    setProgress({ progress: 0 })
    setError(null)
  }, [])

  return {
    exportToCSV,
    isExporting,
    progress,
    error,
    resetExport,
    isReady: !!session?.cube?.token && !!workerRef.current,
  }
}
